import { CapacitorSQLite, SQLiteConnection, SQLiteDBConnection } from '@capacitor-community/sqlite'
import { FileSystem } from '../filesystem/index'
import { getUserTokenPath, FILE_LAST_USER } from '../../consts/filesystem/PATHS.cts'
import { writeFile } from '../filesystem/filesystem'

const DB_NAME_SQLITE = 'aura'
const MIGRATION_FLAG = 'users/_token_migration_done.json'

interface TokenMigrationResult {
  migratedAt: string
  tokensMigrated: number
  users: string[]
}

async function isTokenMigrationDone (filesystem: FileSystem): Promise<boolean> {
  const result = await filesystem.read<TokenMigrationResult>(MIGRATION_FLAG)
  return result != null
}

async function writeTokenMigrationFlag (filesystem: FileSystem, result: TokenMigrationResult): Promise<void> {
  await filesystem.write(MIGRATION_FLAG, result)
}

async function copyLegacyDatabase (sqlite: SQLiteConnection): Promise<boolean> {
  try {
    const isDbExists = await sqlite.isDatabase(DB_NAME_SQLITE)
    if (isDbExists.result) return true

    await sqlite.moveDatabasesAndAddSuffix('default', ['aura.db'])
    const checkAgain = await sqlite.isDatabase(DB_NAME_SQLITE)
    return checkAgain.result ?? false
  } catch (e) {
    console.error('[TokenMigration] copyLegacyDatabase error:', e?.message ?? e)
    return false
  }
}

interface SQLiteInitialData {
  tokens?: Record<string, string>
  lastUser?: string
}

async function readInitialDataFromSQLite (): Promise<SQLiteInitialData | undefined> {
  let sqlite: SQLiteConnection | undefined
  let db: SQLiteDBConnection | undefined

  try {
    sqlite = new SQLiteConnection(CapacitorSQLite)

    const dbAvailable = await copyLegacyDatabase(sqlite)
    if (!dbAvailable) return undefined

    const consistencyResult = await sqlite.checkConnectionsConsistency()
    const connectionResult = await sqlite.isConnection(DB_NAME_SQLITE, false)

    if (consistencyResult.result && connectionResult.result) {
      db = await sqlite.retrieveConnection(DB_NAME_SQLITE, false)
    } else {
      db = await sqlite.createConnection(DB_NAME_SQLITE, false, 'no-encryption', 1, false)
    }

    await db.open()

    const tokensRes = await db.query("SELECT data FROM Initial WHERE id = 'tokens'")
    const lastUserRes = await db.query("SELECT data FROM Initial WHERE id = 'lastUser'")

    let tokens: Record<string, string> | undefined
    if (tokensRes.values?.length) {
      const raw = tokensRes.values[0].data as string
      if (raw) {
        let parsed = JSON.parse(raw)
        if (typeof parsed === 'string') parsed = JSON.parse(parsed)
        tokens = parsed
      }
    }

    let lastUser: string | undefined
    if (lastUserRes.values?.length) {
      const raw = lastUserRes.values[0].data as string
      if (raw) {
        let parsed = JSON.parse(raw)
        if (typeof parsed === 'string') parsed = JSON.parse(parsed)
        lastUser = String(parsed)
      }
    }

    return { tokens, lastUser }
  } catch (e) {
    console.error('[TokenMigration] Error reading initial data from SQLite:', e?.message ?? e)
    return undefined
  } finally {
    try {
      if (db) await db.close()
      if (sqlite) await sqlite.closeConnection(DB_NAME_SQLITE, false)
    } catch (_) {}
  }
}

export async function runTokenMigration (filesystem: FileSystem): Promise<void> {
  console.log('[TokenMigration] Start')

  if (await isTokenMigrationDone(filesystem)) {
    console.log('[TokenMigration] Already done, skipping')
    return
  }

  const data = await readInitialDataFromSQLite()
  if (data == null) {
    console.log('[TokenMigration] No initial data found in SQLite')
    await writeTokenMigrationFlag(filesystem, { migratedAt: new Date().toISOString(), tokensMigrated: 0, users: [] })
    return
  }

  // Migrate lastUser
  if (data.lastUser) {
    const lastUserId = parseInt(data.lastUser, 10)
    if (!isNaN(lastUserId)) {
      await writeFile(FILE_LAST_USER, JSON.stringify(lastUserId))
      console.log(`[TokenMigration] Migrated lastUser: ${lastUserId}`)
    }
  }

  // Migrate tokens
  if (data.tokens == null || Object.keys(data.tokens).length === 0) {
    console.log('[TokenMigration] No tokens to migrate')
    await writeTokenMigrationFlag(filesystem, { migratedAt: new Date().toISOString(), tokensMigrated: 0, users: [] })
    return
  }

  const migratedUsers: string[] = []

  for (const [uid, token] of Object.entries(data.tokens)) {
    if (!token || token === '') continue
    const userId = parseInt(uid, 10)
    if (isNaN(userId)) continue

    try {
      await filesystem.setUser(userId)
      const tokenPath = getUserTokenPath(userId)
      await writeFile(tokenPath, JSON.stringify(token))
      migratedUsers.push(uid)
      console.log(`[TokenMigration] Migrated token for user ${uid}`)
    } catch (e) {
      console.error(`[TokenMigration] Failed for user ${uid}:`, e?.message ?? e)
    }
  }

  await writeTokenMigrationFlag(filesystem, {
    migratedAt: new Date().toISOString(),
    tokensMigrated: migratedUsers.length,
    users: migratedUsers
  })
  console.log(`[TokenMigration] Done — migrated ${migratedUsers.length} tokens`)
}
